<template>
  <div class="product-page news-detail-page">
    <main id="main-content">
      <section class="news-detail-section">
        <div class="container news-detail-container">
          <div v-if="loading && !article" class="news-detail-state">
            <p>加载中…</p>
          </div>

          <template v-else-if="article">
            <nav class="news-breadcrumb" aria-label="面包屑">
              <RouterLink to="/">首页</RouterLink>
              <span class="news-bc-sep" aria-hidden="true">/</span>
              <RouterLink to="/news">新闻中心</RouterLink>
              <span class="news-bc-sep" aria-hidden="true">/</span>
              <span class="news-bc-current">{{ article.title }}</span>
            </nav>

            <header class="news-article-head">
              <div class="news-article-meta">
                <span class="news-article-date">{{ article.date }}</span>
                <span v-if="article.author" class="news-article-author">{{ article.author }}</span>
              </div>
              <h1 class="news-article-title">{{ article.title }}</h1>
              <p v-if="article.summary" class="news-article-digest">{{ article.summary }}</p>
            </header>

            <div v-if="article.contentUrl" class="news-article-body">
              <iframe
                ref="iframeRef"
                :style="{ height: iframeHeight }"
                :src="article.contentUrl"
                title="新闻正文"
                frameborder="0"
                @load="onIframeLoad"
              />
            </div>
            <p v-else class="news-article-miss-body">暂无正文内容</p>

            <footer class="news-article-foot">
              <div class="news-adj">
                <RouterLink
                  v-if="neighbors.prev"
                  class="news-adj-link"
                  :to="{ name: 'news-detail', params: { id: neighbors.prev.id } }"
                >
                  <span class="news-adj-label">上一篇</span>
                  <span class="news-adj-title">{{ neighbors.prev.title }}</span>
                </RouterLink>
                <span v-else class="news-adj-empty">上一篇：无</span>

                <RouterLink
                  v-if="neighbors.next"
                  class="news-adj-link news-adj-link--next"
                  :to="{ name: 'news-detail', params: { id: neighbors.next.id } }"
                >
                  <span class="news-adj-label">下一篇</span>
                  <span class="news-adj-title">{{ neighbors.next.title }}</span>
                </RouterLink>
                <span v-else class="news-adj-empty news-adj-empty--next">下一篇：无</span>
              </div>

              <div class="news-foot-actions">
                <a
                  v-if="article.sourceWechatUrl"
                  class="news-source-link"
                  :href="article.sourceWechatUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  查看微信公众号原文
                </a>
              </div>
            </footer>
          </template>

          <div v-else class="news-detail-state news-detail-miss">
            <p>未找到该新闻，可能已下线或链接有误。</p>
            <RouterLink to="/news" class="btn-primary news-back-btn">返回新闻列表</RouterLink>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useHead } from '@vueuse/head';
import { useFrontCmsStore } from '@/stores/frontCms';
import { mapFrontNewsDetail, mapNewsNeighborsFromDetail } from '@/utils/frontCmsMappers';
import '@/styles/newhome/product-page.css';

const NEWS_LIST_PAGE_SIZE = 12;
const siteName = 'Hyxino 科技门户';

const route = useRoute();
const cms = useFrontCmsStore();
const { syncing, newsPage } = storeToRefs(cms);

const newsId = computed(() => String(route.params.id ?? ''));

const loading = computed(() => !!syncing.value.newsDetail);

const article = computed(() => {
  const detail = cms.getNewsDetail(newsId.value);
  return detail ? mapFrontNewsDetail(detail) : null;
});

const neighbors = computed(() => {
  const detail = cms.getNewsDetail(newsId.value);
  return detail ? mapNewsNeighborsFromDetail(detail) : { prev: null, next: null };
});

const iframeRef = ref<HTMLIFrameElement | null>(null);
const iframeHeight = ref('560px');

async function loadDetail(id: string) {
  if (!id) return;
  cms.hydrateFromCache();
  if (!newsPage.value.records.length) {
    await cms.syncNewsPage(1, NEWS_LIST_PAGE_SIZE);
  }
  await cms.syncNewsDetail(id);
}

watch(newsId, (id) => void loadDetail(id), { immediate: true });

watch(
  () => article.value,
  (a) => {
    iframeHeight.value = '560px';
    useHead({
      title: a ? `${a.title} | ${siteName}` : `新闻详情 | ${siteName}`,
      meta: [
        { name: 'description', content: a?.summary ?? '海曦技术新闻详情。' },
        { property: 'og:title', content: a ? `${a.title} | ${siteName}` : siteName },
        { property: 'og:description', content: a?.summary ?? '' },
        ...(a?.thumbUrl ? [{ property: 'og:image', content: a.thumbUrl }] : [])
      ]
    });
  },
  { immediate: true }
);

const setIframeHeight = () => {
  const iframe = iframeRef.value;
  if (!iframe) return;
  try {
    const doc = iframe.contentDocument ?? iframe.contentWindow?.document;
    if (!doc) return;
    const body = doc.body;
    const html = doc.documentElement;
    const height = Math.max(
      body.scrollHeight,
      body.offsetHeight,
      html.scrollHeight,
      html.offsetHeight
    );
    if (height > 0) iframeHeight.value = `${height}px`;
  } catch (error) {
    console.warn('[NewsDetailPage] iframe auto height failed', error);
  }
};

const requestIframeHeight = () => {
  const iframe = iframeRef.value;
  if (iframe?.contentWindow) {
    iframe.contentWindow.postMessage({ type: 'iframe-request-height' }, '*');
  }
};

const handleIframeMessage = (event: MessageEvent) => {
  if (event.data?.type === 'iframe-height' && typeof event.data.height === 'number') {
    iframeHeight.value = `${event.data.height}px`;
  }
};

const onIframeLoad = () => {
  setIframeHeight();
  requestIframeHeight();
};

onMounted(() => {
  window.addEventListener('message', handleIframeMessage);
});

onUnmounted(() => {
  window.removeEventListener('message', handleIframeMessage);
});
</script>

<style scoped lang="scss">
.news-detail-page {
  min-height: 100vh;
}

.news-detail-section {
  padding: calc(var(--nav-height) + 48px) 0 96px;
}

.news-detail-container {
  max-width: 880px;
}

.news-detail-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  min-height: 40vh;
  text-align: center;
  color: var(--fg-muted);

  p {
    margin: 0;
    font-size: 15px;
  }
}

.news-breadcrumb {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 0;
  margin-bottom: 32px;
  font-size: 13px;
  color: var(--fg-muted);
  line-height: 1.5;

  a {
    color: var(--link-dark);
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}

.news-bc-sep {
  margin: 0 10px;
  color: rgba(255, 255, 255, 0.28);
}

.news-bc-current {
  color: rgba(245, 245, 247, 0.72);
  max-width: min(100%, 28em);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.news-article-head {
  margin-bottom: 36px;
}

.news-article-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.news-article-date {
  display: inline-flex;
  align-items: center;
  padding: 4px 14px;
  border-radius: 8px;
  border: 1px solid rgba(239, 239, 239, 0.2);
  background: rgba(28, 32, 41, 0.5);
  font-size: 13px;
  color: var(--fg-muted);
  letter-spacing: -0.12px;
}

.news-article-author {
  font-size: 14px;
  font-weight: 500;
  color: #45c1cf;
}

.news-article-title {
  margin: 0 0 16px;
  font-family: 'Noto Sans SC', -apple-system, sans-serif;
  font-size: clamp(28px, 4vw, 40px);
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: -0.02em;
  color: #fff;
}

.news-article-digest {
  margin: 0;
  font-size: 16px;
  line-height: 1.65;
  color: var(--fg-hero-secondary);
}

.news-article-body {
  margin-bottom: 40px;
  border-radius: 8px;
  border: 1px solid rgba(239, 239, 239, 0.2);
  background: #fff;
  overflow: hidden;

  iframe {
    display: block;
    width: 100%;
    border: none;
    background: #fff;
  }
}

.news-article-miss-body {
  margin: 0 0 40px;
  padding: 40px 24px;
  text-align: center;
  color: var(--fg-muted);
  border-radius: 8px;
  border: 1px solid rgba(239, 239, 239, 0.2);
  background: linear-gradient(180deg, rgba(28, 32, 41, 0.5) 0%, rgba(28, 32, 41, 0) 100%);
}

.news-article-foot {
  display: flex;
  flex-direction: column;
  gap: 28px;
  padding-top: 8px;
}

.news-adj {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.news-adj-link {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px 22px;
  border-radius: 8px;
  border: 1px solid rgba(239, 239, 239, 0.2);
  background: linear-gradient(180deg, rgba(28, 32, 41, 0.5) 0%, rgba(28, 32, 41, 0) 100%);
  color: inherit;
  text-decoration: none;
  transition:
    transform 0.3s,
    border-color 0.3s;

  &:hover {
    transform: translateY(-2px);
    border-color: var(--border-hover);
  }
}

.news-adj-link--next {
  text-align: right;
  grid-column: 2;
}

.news-adj-label {
  font-size: 12px;
  color: var(--fg-muted);
  letter-spacing: 0.06em;
}

.news-adj-title {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.45;
  color: #fff;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

.news-adj-empty {
  padding: 20px 22px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.28);
  border-radius: 8px;
  border: 1px dashed rgba(239, 239, 239, 0.12);
}

.news-adj-empty--next {
  grid-column: 2;
  text-align: right;
}

.news-foot-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 20px;
}

.news-source-link {
  font-size: 14px;
  font-weight: 500;
  color: var(--link-dark);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.news-back-btn {
  text-decoration: none;
}

@media (max-width: 640px) {
  .news-detail-section {
    padding: calc(var(--nav-height) + 28px) 0 64px;
  }

  .news-adj {
    grid-template-columns: 1fr;
  }

  .news-adj-link--next,
  .news-adj-empty--next {
    grid-column: 1;
    text-align: left;
  }
}
</style>
